import {
    AddBoxOutlined,
    ArrowBack,
    ArrowForward,
    ClearOutlined,
    CloseOutlined,
    FileDownloadOutlined,
    InventoryOutlined,
    PlaylistRemoveOutlined,
    RefreshOutlined,
    SearchOutlined,
    SettingsOutlined,
} from "@mui/icons-material";

import {
    Box,
    CardMedia,
    FormControl,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    styled,
    Switch,
    SwitchProps,
    TextField,
    Tooltip,
    Toolbar,
    Typography,
    useMediaQuery,
} from "@mui/material";

import {
    DataGrid,
    GridColDef,
    GridRowsProp,
    GridSortDirection,
} from "@mui/x-data-grid";

import { GeofenceType } from "../types/types";
import { GRID_DEFAULT_LOCALE_TEXT } from "../resources/location/utils/location";
import { MenuItem as MenuIt } from "primereact/menuitem";
import { Dispatch, SetStateAction, useMemo, useRef, useState } from "react";
import { isImpersonationLocked } from "../utils/impersonation";
// import Pager from "./Pager";

const items: MenuIt[] = [
    {
        label: 'PDF',
    },
    {
        label: 'Excel',
    },
    {
        label: "Excel simples",
    }
];

const headerActionButtonSx = {
    width: 40,
    height: 40,
    flexShrink: 0,
    color: "#fff",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    "&.Mui-disabled": {
        color: "rgba(255, 255, 255, 0.35)",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
};

const headerIconButtonClass = "!tw-h-10 !tw-w-10 !tw-rounded-lg !tw-text-white tw-transition-all tw-duration-200 tw-ease-gtr-smooth hover:-tw-translate-y-0.5 hover:tw-shadow-md focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-white/80";

/**
 * GridTemplate - Component that render a grid with given informations
 * @component
 */
const GridTemplate = ({
    headerName,
    withOptions,
    withFilter,
    sortField,
    order,
    isLoading,
    rows,
    columns,
    isHiddenMenu,
    query,
    setQuery,
    disableAddButton,
    disableOptionsButton = false,
    optionsDisabledReason,
    filterPlaceholder,
    disableReportButton,
    enabledCheckListButton = false,
    disableDeactivatedButton = false,
    onRefresh,
    onClickRow,
    onExport,
    onAdd,
    style,
    contentHeight,
    containerHeight,
    withNavigation = false,
    isLastPage,
    handlePrevious,
    handleNext,
    totalPerPage,
    currentPage,
    totalElements,
    withCustomerParameter = false,
    whithCustomerFilterOption = false,
    onSetCustomerFilter,
    customerParameter,
    customHeaderOptions,
    extraOptions
}: PropTypes) => {
    const matches = useMediaQuery("(max-width:430px)");
    const fillAvailableHeight = containerHeight === "100%";
    const [open, setOpen] = useState<boolean>(false);
    const anchorRef = useRef<any>(null);

    const NoRowsOverlay = () => (
        <Box className="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-p-6 tw-text-center">
            <CardMedia
                component="img"
                className="grid-empty-image !tw-h-auto !tw-max-h-64 !tw-w-full !tw-max-w-[350px] !tw-object-contain dark:!tw-hidden"
                image={require("../assets/files/nocontent.png")}
                title="Nenhum dado encontrado"
            />
            <Typography className="grid-empty-message !tw-font-prometo !tw-text-lg !tw-font-bold !tw-text-gtr-green dark:!tw-text-slate-200">
                Nenhum dado encontrado.
            </Typography>
        </Box>
    );
    
    const IOSSwitch = useMemo(() => styled((props: SwitchProps) => (
        <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
    ))(({ theme }) => ({
        width: 42,
        height: 26,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: '#65C466',
                    opacity: 1,
                    border: 0,
                    ...theme.applyStyles('dark', {
                        backgroundColor: '#2ECA45',
                    }),
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: '#33cf4d',
                border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.palette.grey[100],
                ...theme.applyStyles('dark', {
                    color: theme.palette.grey[600],
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.7,
                ...theme.applyStyles('dark', {
                    opacity: 0.3,
                }),
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
        },
        '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
                duration: 500,
            }),
            ...theme.applyStyles('dark', {
                backgroundColor: '#39393D',
            }),
        },
    })), []);

    return (
        <Box
            className="grid-box-out-container tw-flex tw-w-full tw-items-center tw-justify-center tw-bg-transparent tw-p-0.5"
            style={{
                height: fillAvailableHeight ? "100%" : containerHeight ? "unset" : "100vh",
            }}
        >
            <Box
                className="grid-container container container-fluid tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-2.5 tw-shadow-md tw-transition-all tw-duration-300 tw-ease-gtr-smooth"
                style={{
                    marginTop: "2%",
                    width: containerHeight ? "100%" : (matches ? "100%" : (isHiddenMenu ? "70vw" : "65vw")),
                    ...(fillAvailableHeight ? { height: "100%", minHeight: 0, display: "flex", flexDirection: "column" as const } : {}),
                    ...style,
                }}
                sx={fillAvailableHeight ? { "& > .grid-header-bar": { flexShrink: 0 } } : undefined}
            >
                {headerName && (
                    <Toolbar
                        disableGutters
                        className="grid-header-bar tw-relative tw-mb-2 tw-min-h-[64px] tw-rounded-xl tw-bg-gtr-sidebar tw-p-2.5 tw-font-prometo tw-text-white tw-shadow-sm"
                    >
                        <Box
                            className="header-actions tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2.5"
                        >
                            <Box
                                className="header-box-title tw-flex tw-min-h-10 tw-w-full tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 md:tw-w-auto md:tw-flex-[1_1_220px]"
                            >
                                {withNavigation || window.location.pathname.includes("/customers") ? (
                                    <Box className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                                        <Tooltip title="Voltar" arrow>
                                            <IconButton
                                                className={`${headerIconButtonClass} refresh-button-icon !tw-bg-gtr-green hover:!tw-bg-gtr-green-hover`}
                                                aria-label="Voltar"
                                                onClick={() => window.history.back()}
                                                sx={headerActionButtonSx}
                                            >
                                                <ArrowBack />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Avançar" arrow>
                                            <IconButton
                                                className={`${headerIconButtonClass} !tw-bg-gtr-green hover:!tw-bg-gtr-green-hover`}
                                                aria-label="Avançar"
                                                onClick={() => window.history.go(1)}
                                                sx={headerActionButtonSx}
                                            >
                                                <ArrowForward />
                                            </IconButton>
                                        </Tooltip>
                                        <Typography noWrap className="!tw-ml-1 !tw-font-prometo !tw-text-base !tw-font-bold !tw-text-white sm:!tw-text-lg">
                                            {headerName}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography noWrap className="!tw-font-prometo !tw-text-base !tw-font-bold !tw-text-white sm:!tw-text-lg">
                                        {headerName}
                                    </Typography>
                                )}
                            </Box>

                            {withFilter && (
                                <Box className="list-options tw-order-last tw-w-full tw-min-w-0 tw-flex-[2] md:tw-order-none md:tw-w-auto md:tw-min-w-[260px] md:tw-max-w-[420px] md:tw-flex-[1_1_320px]">
                                    <FormControl className="form-group tw-w-full">
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            name="name"
                                            type="text"
                                            autoComplete="off"
                                            placeholder={filterPlaceholder}
                                            className="tw-w-full"
                                            value={query}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                setQuery(event.target.value);
                                            }}
                                            inputProps={{ "aria-label": filterPlaceholder || "Pesquisar na listagem" }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchOutlined style={{ color: "rgba(255, 255, 255, 0.7)" }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: query ? (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Limpar pesquisa" arrow placement="top">
                                                            <IconButton
                                                                size="small"
                                                                aria-label="Limpar pesquisa"
                                                                onClick={() => setQuery("")}
                                                                style={{ color: "rgba(255, 255, 255, 0.7)" }}
                                                            >
                                                                <ClearOutlined fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ) : null,
                                                style: {
                                                    fontFamily: "Prometo",
                                                    color: "#fff",
                                                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                                                    borderRadius: "8px",
                                                    paddingRight: 8,
                                                },
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    minHeight: 44,
                                                    transition: "background-color 200ms ease, box-shadow 200ms ease",
                                                    "& fieldset": { border: "none" },
                                                    "&:hover fieldset": { border: "none" },
                                                    "&.Mui-focused fieldset": { border: "1px solid rgba(255, 255, 255, 0.5)" },
                                                    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.10)" },
                                                },
                                                "& input::placeholder": {
                                                    color: "rgba(255, 255, 255, 0.72)",
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            )}

                            <Box className={customHeaderOptions ? "tw-ml-auto tw-flex tw-flex-none tw-items-center tw-gap-2.5" : "tw-contents"}>
                            {customHeaderOptions && (
                                <Box className="tw-flex tw-flex-none tw-items-center tw-gap-2">
                                    {customHeaderOptions}
                                </Box>
                            )}

                            {withOptions && (
                                <Box sx={{ position: "relative", width: "250px", height: "45px", marginLeft: "auto", marginRight: "100px", flexShrink: 0 }}>
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            right: 0,
                                            top: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            flexDirection: "row-reverse",
                                            width: "45px",
                                            height: "45px",
                                            overflow: "hidden",
                                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                            borderRadius: "25px",
                                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                                            zIndex: 10,
                                            "&:hover": disableOptionsButton ? {} : {
                                                width: "250px",
                                                backgroundColor: "rgba(255, 255, 255, 0.15)",
                                                paddingRight: "5px",
                                            },
                                            "&:hover .options-container": disableOptionsButton ? {} : {
                                                opacity: 1,
                                                transform: "translateX(0)",
                                            }
                                        }}
                                    >
                                    <Tooltip title={disableOptionsButton ? optionsDisabledReason || "Mais opções desabilitadas" : "Mais opções"} arrow>
                                        <span style={{ display: "inline-flex", flexShrink: 0 }}>
                                            <IconButton
                                                aria-label="Mostrar mais opções"
                                                disabled={disableOptionsButton}
                                                onClick={(e) => e.stopPropagation()}
                                                sx={{
                                                    color: "#ffff",
                                                    minWidth: "45px",
                                                    height: "45px",
                                                    "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
                                                }}
                                            >
                                                <SettingsOutlined />
                                            </IconButton>
                                        </span>
                                    </Tooltip>

                                    {!disableOptionsButton && <Box
                                        className="options-container"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            opacity: 0,
                                            transform: "translateX(20px)",
                                            transition: "all 0.3s ease-out",
                                            paddingRight: "10px",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        {whithCustomerFilterOption && (
                                            <IOSSwitch
                                                title="Filtro por cliente"
                                                onChange={(event: any) => {
                                                    const checked = event.target.checked;
                                                    onSetCustomerFilter?.(checked);
                                                }}
                                            />
                                        )}

                                        {disableDeactivatedButton && (
                                            <>
                                                <Tooltip title="Exportar" arrow>
                                                    <span>
                                                        <IconButton
                                                            ref={(ref) => anchorRef.current = ref}
                                                            aria-label="Exportar"
                                                            disabled={!enabledCheckListButton}
                                                            onClick={() => setOpen(true)}
                                                            sx={{
                                                                background: "#00b700",
                                                                width: "32px",
                                                                height: "32px",
                                                                "&.Mui-disabled": {
                                                                    background: "#dbdbdb48",
                                                                }
                                                            }}
                                                        >
                                                            <InventoryOutlined style={{ color: !enabledCheckListButton ? "#dbdbdb48" : "#ffff", fontSize: "1.1rem" }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <Menu
                                                    open={open}
                                                    anchorEl={() => anchorRef.current as HTMLElement}
                                                    onClose={() => { setOpen(false) }}
                                                >
                                                    {items.map((option: MenuIt) => (
                                                        <MenuItem key={option?.id} onClick={(e: any) => {
                                                            setOpen(false);
                                                            onExport(option.label!!.toLocaleLowerCase())
                                                        }}>
                                                            {` ${option.label}`}
                                                        </MenuItem>
                                                    ))}
                                                </Menu>
                                            </>
                                        )}

                                        {!disableReportButton && (
                                            <>
                                                <Tooltip title="Exportar" arrow>
                                                    <IconButton
                                                        ref={(ref) => anchorRef.current = ref}
                                                        aria-label="Exportar"
                                                        onClick={() => setOpen(true)}
                                                        sx={{
                                                            background: "#0ec8e6",
                                                            width: "32px",
                                                            height: "32px"
                                                        }}
                                                    >
                                                        <FileDownloadOutlined style={{ color: "#ffff", fontSize: "1.1rem" }} />
                                                    </IconButton>
                                                </Tooltip>

                                                <Menu
                                                    open={open}
                                                    anchorEl={() => anchorRef.current as HTMLElement}
                                                    onClose={() => { setOpen(false) }}
                                                >
                                                    {items.map((option: MenuIt) => (
                                                        <MenuItem key={option?.id} onClick={(e: any) => {
                                                            setOpen(false);
                                                            onExport(option.label!!.toLocaleLowerCase())
                                                        }}>
                                                            {` ${option.label}`}
                                                        </MenuItem>
                                                    ))}
                                                </Menu>
                                            </>
                                        )}

                                        {!disableDeactivatedButton && (
                                            <Tooltip title="Ver inativos" arrow>
                                                <IconButton
                                                    aria-label="Ver registros inativos"
                                                    onClick={() => { onRefresh("", "deactivated") }}
                                                    sx={{
                                                        background: "rgba(220, 53, 69, 1)",
                                                        width: "32px",
                                                        height: "32px",
                                                    }}
                                                >
                                                    <PlaylistRemoveOutlined style={{ color: "#ffff", fontSize: "1.1rem" }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {extraOptions}

                                        <Tooltip title={disableAddButton ? "Desabilitado" : "Novo"} arrow>
                                            <span>
                                                <IconButton
                                                    aria-label="Criar novo registro"
                                                    onClick={() => {
                                                        onAdd();
                                                    }}
                                                    disabled={disableAddButton || isImpersonationLocked()}
                                                    sx={{
                                                        background: "#00b700",
                                                        width: "32px",
                                                        height: "32px",
                                                        "&.Mui-disabled": {
                                                            background: "#dbdbdb48",
                                                        }
                                                    }}
                                                >
                                                    <AddBoxOutlined style={{
                                                        color: disableAddButton ? "#dbdbdb48" : "#ffff",
                                                        fontSize: "1.1rem"
                                                    }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>}
                                </Box>
                                </Box>
                            )}

                            <Tooltip title="Atualizar" arrow>
                                <IconButton
                                    aria-label="Atualizar listagem"
                                    onClick={() => {
                                        onRefresh();
                                    }}
                                    sx={{
                                        position: customHeaderOptions ? "static" : "absolute",
                                        width: customHeaderOptions ? 40 : undefined,
                                        height: customHeaderOptions ? 40 : undefined,
                                        flexShrink: 0,
                                        right: 60,
                                        top: "50%",
                                        transform: customHeaderOptions ? "none" : "translateY(-50%)",
                                        color: "#ffff",
                                        zIndex: 5,
                                        transition: "all 0.2s ease-out allow-discrete",
                                    }}
                                >
                                    <RefreshOutlined />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Fechar" arrow>
                                <IconButton
                                    aria-label="Fechar listagem"
                                    onClick={() => {
                                        window.location.replace("/");
                                    }}
                                    sx={{
                                        position: customHeaderOptions ? "static" : "absolute",
                                        width: customHeaderOptions ? 40 : undefined,
                                        height: customHeaderOptions ? 40 : undefined,
                                        flexShrink: 0,
                                        right: 10,
                                        top: "50%",
                                        transform: customHeaderOptions ? "none" : "translateY(-50%)",
                                        color: "#ffff",
                                        zIndex: 5,
                                        transition: "all 0.2s ease-out allow-discrete",
                                    }}
                                >
                                    <CloseOutlined />
                                </IconButton>
                            </Tooltip>
                            </Box>
                        </Box>
                    </Toolbar>
                )}

                {withCustomerParameter && (
                    <Toolbar
                        disableGutters
                        className="grid-parameter-filter grid-header-bar tw-relative tw-mb-2 tw-min-h-[52px] tw-rounded-xl tw-border-0 tw-bg-gtr-sidebar tw-p-2 tw-font-prometo tw-text-white"
                        sx={{
                            alignItems: "flex-start",
                            minHeight: "52px !important",
                            padding: 1,
                            color: "#fff",
                            backgroundImage: "linear-gradient(to right, #006C77, #09414A)",
                        }}
                    >
                        <Box
                            className={`header-actions tw-flex tw-w-full tw-flex-col tw-gap-2 ${!headerName ? "tw-pr-12" : ""}`}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                alignItems: "stretch",
                                gap: 1,
                                paddingRight: !headerName ? 6 : 0,
                                "& > *": {
                                    flex: { xs: "1 1 auto", md: "1 1 0" },
                                    width: "100% !important",
                                    maxWidth: "none",
                                    minWidth: 0,
                                    margin: "0 !important",
                                    padding: "0 !important",
                                },
                                "& .MuiAutocomplete-root, & .MuiFormControl-root": {
                                    width: "100%",
                                    margin: 0,
                                },
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    color: "#fff",
                                    backgroundColor: "rgba(255,255,255,0.15)",
                                    transition: "box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease",
                                },
                                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "transparent", borderWidth: "1px" },
                                "& .MuiOutlinedInput-root:not(.Mui-disabled):hover": { backgroundColor: "rgba(255,255,255,0.20)" },
                                "& .MuiOutlinedInput-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent", borderWidth: "1px" },
                                "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": { color: "rgba(255,255,255,0.86)" },
                                "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(255,255,255,0.10)" },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.55)", borderWidth: "1px" },
                                "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.72)", opacity: 1 },
                                "& .MuiSelect-icon, & .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator, & .visibility-password": { color: "rgba(255,255,255,0.82)" },
                                "& .Mui-disabled": { WebkitTextFillColor: "rgba(255,255,255,0.48)" },
                                "& .form-group": {
                                    marginBottom: 0,
                                },
                            }}
                        >
                            {customerParameter}
                        </Box>
                        {!headerName && (
                            <Tooltip title="Fechar" arrow>
                                <IconButton
                                    aria-label="Fechar listagem"
                                    onClick={() => {
                                        window.location.replace("/");
                                    }}
                                    className="!tw-absolute !tw-right-2 !tw-h-9 !tw-w-9 !tw-rounded-lg !tw-text-white tw-transition-all tw-duration-200 hover:!tw-bg-white/15"
                                >
                                    <CloseOutlined fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Toolbar>
                )}

                <Box
                    className={`tw-mt-0.5 tw-overflow-hidden tw-rounded-xl tw-bg-white ${isLoading ? "tw-flex tw-items-center tw-justify-center" : ""}`}
                    style={{
                        fontFamily: "Prometo",
                        height: fillAvailableHeight ? "auto" : contentHeight ? contentHeight: "740px",
                        ...(fillAvailableHeight ? { flex: "1 1 0", minHeight: 0 } : {}),
                    }}
                >
                    {!isLoading ? (
                        <>
                        <DataGrid
                            style={{
                                margin: 0,
                                fontFamily: "Prometo",
                                height: fillAvailableHeight ? "100%" : contentHeight ? contentHeight : "740px",
                            }}
                            className="datagrid-selectable !tw-rounded-xl !tw-border-slate-200"
                            sx={{
                                color: "#0C79AD",
                                fontWeight: 600,
                                fontSize: { xs: 14, sm: 16 },
                                "& .MuiDataGrid-columnHeaders": {
                                    backgroundColor: "#00B700",
                                    borderBottom: "1px solid #dbe3e6",
                                    color: "#fff",
                                },
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: 700,
                                    color: "#fff",
                                },
                                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                                    outline: "none",
                                },
                                "& .MuiDataGrid-row": {
                                    transition: "background-color 180ms ease",
                                },
                                "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "rgba(0, 108, 119, 0.045)",
                                },
                                "& .MuiDataGrid-cell": {
                                    borderBottomColor: "#eef1f3",
                                },
                                "& .MuiDataGrid-footerContainer": {
                                    minHeight: 52,
                                    borderTop: "1px solid #e2e8f0",
                                    backgroundColor: "#f8fafc",
                                },
                                "& .MuiTablePagination-root": {
                                    color: "#475569",
                                },
                                "& .MuiTablePagination-actions .MuiIconButton-root": {
                                    borderRadius: "8px",
                                    transition: "background-color 180ms ease, color 180ms ease",
                                },
                                "& .MuiTablePagination-actions .MuiIconButton-root:hover": {
                                    color: "#006C77",
                                    backgroundColor: "rgba(0, 108, 119, 0.08)",
                                },
                            }}
                            slots={{
                                noRowsOverlay: NoRowsOverlay,
                            }}
                            rows={rows}
                            localeText={GRID_DEFAULT_LOCALE_TEXT}
                            onRowClick={({ row }) => {
                                onClickRow(row);
                            }}
                            columns={columns}
                            paginationMode="server"
                            // rowCount={totalElements}
                            paginationModel={{
                                page: currentPage,
                                pageSize: totalPerPage,
                            }}
                            slotProps={{
                                pagination: {
                                    count: totalElements,
                                    page: currentPage,
                                    nextIconButtonProps: {
                                        onClick: () => handleNext(rows[0].activated ? "activated" : "deactivated", currentPage + 1, 100),
                                        disabled: isLastPage === true

                                    },
                                    backIconButtonProps: {
                                        onClick: () => handleNext(rows[0].activated ? "activated" : "deactivated", currentPage - 1, 100),
                                        disabled: currentPage === 0
                                    },
                                }
                            }}
                            disableColumnMenu
                            // hideFooterPagination
                            pageSizeOptions={[100]}
                            density="standard"
                            checkboxSelection={false}
                            getRowClassName={(row) =>
                                row.row.approved ? "disabled-row" : ""
                            }
                            disableRowSelectionOnClick
                            rowHeight={40}

                        />
                        {/*<Box style={{ margin: -30}}>
                            <Pager
                                page={currentPage}
                                pageCount={Math.ceil(totalElements / 100)}
                                onChange={(newPage) =>
                                    handleNext(
                                        rows[0].activated ? "activated" : "deactivated",
                                        newPage,
                                        totalPerPage
                                    )
                                }
                            />
                        </Box>*/}
                        </>
                    ) : (
                        <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-p-5">
                            <div className="tracker-loading">
                                <div className="ld-tracker">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                            </div>
                        </div>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

type PropTypes = {
    headerName: string;
    withOptions: boolean;
    withFilter: boolean;
    sortField: string;
    order: GridSortDirection;
    isLoading: boolean;
    rows: GridRowsProp<any>;
    columns: GridColDef[];
    isHiddenMenu: boolean;
    disableAddButton?: boolean;
    disableOptionsButton?: boolean;
    optionsDisabledReason?: string;
    disableReportButton?: boolean;
    enabledCheckListButton?: boolean;
    disableDeactivatedButton?: boolean,
    query: string;
    setQuery: (value: string) => void;
    filterPlaceholder: string;
    onRefresh: (...args: any) => void;
    onClickRow: (value: GeofenceType) => void;
    onExport: (type: string) => Promise<void>;
    onAdd: () => void;
    style?: React.CSSProperties;
    contentHeight?: string;
    containerHeight?: string;
    withNavigation?: boolean;
    handleNext: (...args: any) => void;
    handlePrevious: () => void;
    isLastPage: boolean;
    totalPerPage: number;
    currentPage: number;
    totalElements: number;
    withCustomerParameter?: boolean;
    whithCustomerFilterOption?: boolean;
    onSetCustomerFilter?: Dispatch<SetStateAction<boolean>>;
    customerParameter?: React.ReactNode;
    customHeaderOptions?: React.ReactNode;
    extraOptions?: React.ReactNode;
};

export default GridTemplate;
